// Types for Owner Offboarding (db/migrations/1500). Every date, step and
// refusal comes from Postgres; this file only names the shapes and the labels.
export type OffboardCar = {
  vehicle_id: string
  plate: string
  vehicle_name: string | null
  status: string
  owner_name: string | null
  owner_email: string | null
  manager_name: string | null
  has_tracker: boolean
  off_road: boolean
}

export type StepKey = 'email' | 'insurance' | 'assess' | 'repairs' | 'weekly' | 'deinstall' | 'handback' | 'reason'

export type OffboardStep = {
  key: StepKey
  title: string
  owner: string
  start: string
  target: string
  status: string
  task_ref: number | null
  done_at: string | null
  done_by_name: string | null
  note: string | null
  overdue: boolean
}

export type OffboardCase = {
  id: string
  vehicle_id: string
  plate: string
  vehicle_name: string | null
  vehicle_status: string
  owner_name: string | null
  owner_email: string | null
  manager_name: string | null
  has_tracker: boolean
  notice_date: string
  end_date: string
  day: number
  opened_at: string
  opened_by_name: string | null
  open_note: string | null
  email_sent_at: string | null
  email_status: 'queued' | 'sent' | 'failed' | 'held' | null
  email_failure: string | null
  assessment: 'minor' | 'major' | null
  assessment_location: string | null
  assessment_notes: string | null
  assessed_at: string | null
  assessed_by_name: string | null
  off_road: boolean
  insurance_cancel_requested_on: string | null
  insurance_cover_ends: string | null
  repairs_done_on: string | null
  deinstalled_on: string | null
  handed_back_on: string | null
  exit_reason: string | null
  exit_reason_note: string | null
  closed_at: string | null
  closed_reason: string | null
  closed_by_name: string | null
  parent_task_ref: number | null
  inspection: OffboardInspection | null
  steps: OffboardStep[]
  weekly_updates: { at: string; by: string | null; body: string }[]
}

/** One row of the checklist (owner_offboarding_inspection_item, migration 9700). */
export type InspectionItem = {
  key: string
  section: string
  section_label: string
  kind: 'photo' | 'check'
  label: string
  help: string | null
  photo: 'always' | 'on_fail' | 'never'
  allows_na: boolean
}

export type InspectionResult = 'photo' | 'pass' | 'fail' | 'na'

/** The newest inspection filed on a case. */
export type OffboardInspection = {
  id: string
  inspected_on: string
  created_at: string
  by_name: string | null
  odometer_km: number
  notes: string | null
  fail_count: number
  photo_count: number
  faults_accepted_at: string | null
  faults_accepted_reason: string | null
  faults_accepted_by_name: string | null
  earlier: number
  answers: { key: string; label: string; section_label: string; result: InspectionResult; note: string | null; photo: string | null }[]
}

/** Whether an answer is complete, by the same rule owner_offboarding_inspect_v1 applies. */
export function answerMissing(
  item: InspectionItem,
  a: { result?: string; note?: string; photo?: string } | undefined,
): string | null {
  if (item.kind === 'photo') return a?.photo ? null : 'Take the photo.'
  const r = a?.result
  if (r !== 'pass' && r !== 'fail' && r !== 'na') return 'Answer it.'
  if (r === 'na' && !item.allows_na) return 'Mark it working or a fault.'
  if (r === 'fail' && !a?.note?.trim()) return 'Say what is wrong.'
  if (!a?.photo && ((item.photo === 'always' && r !== 'na') || (item.photo === 'on_fail' && r === 'fail'))) return 'Add a photo.'
  return null
}

export type OffboardPage = {
  can_act: boolean
  who_can_act: string | null
  contact_name: string | null
  cars: OffboardCar[]
  cases: OffboardCase[]
}

export type LetterPreview = {
  ok: boolean
  to: string | null
  reply_to: string | null
  subject: string
  body: string
  owner_name: string | null
  plate: string
}

// The reasons trg_task_offboarding_close_guard (0124) accepts.
export const EXIT_REASONS = ['owner exit', 'contract ended', 'repossession', 'write off', 'fleetly exit', 'other'] as const

export const EMAIL_STATUS_LABEL: Record<string, string> = {
  queued: 'Letter queued',
  sent: 'Letter sent',
  failed: 'Letter failed',
  held: 'Letter held',
}

/** Today in Johannesburg, as YYYY-MM-DD, for the date inputs. */
export function todaySast(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Johannesburg' })
}
